import { Button, Card, Checkbox, InputNumber, Slider } from '@lockerpm/design'
import { DEFAULT_PASSWORD_NOTION_TABLE, PASSWORD_GENERATOR_ALIAS } from '@/config/constants'
import { ParsePasswordScoreText, toThousandFilter } from '@/utils/common'
import { useEffect, useMemo, useState } from 'react'

import BigNumber from 'bignumber.js'
import CheckCircle from '@/public/icons/check-circle.svg'
import ModalCopied from './ModalCopied'
import Refresh from '@/public/images/password-generator/Refresh.svg'
import Shield from '@/public/images/password-generator/Shield.svg'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { fetchPasswordGeneratorApp } from '@/services/resource'
import { useRouter } from 'next/router'

var duration = require('dayjs/plugin/duration')
dayjs.extend(duration)
const CardTitle = ({ password, passwordStrength, randomPassword }) => {
  const timeToCrack = useMemo(() => {
    const crackTimeInSeconds = new BigNumber(
      passwordStrength?.crack_times_seconds?.offline_slow_hashing_1e4_per_second || 0
    )

    const units = [
      { label: 'năm', seconds: new BigNumber(60 * 60 * 24 * 365), format: '> 100 tỷ năm' },
      { label: 'tháng', seconds: new BigNumber(60 * 60 * 24 * 30) },
      { label: 'ngày', seconds: new BigNumber(60 * 60 * 24) },
      { label: 'giờ', seconds: new BigNumber(60 * 60) },
      { label: 'phút', seconds: new BigNumber(60) },
    ]

    for (const { label, seconds, format } of units) {
      if (crackTimeInSeconds.isGreaterThan(seconds)) {
        const time = crackTimeInSeconds.div(seconds)
        if (time.isGreaterThan(new BigNumber(100000000000))) {
          return format
        }
        return `${toThousandFilter(time.toFixed(2))} ${label}`
      }
    }

    return crackTimeInSeconds.isGreaterThan(new BigNumber(1))
      ? `${crackTimeInSeconds.toFixed()} giây`
      : '< 1 giây'
  }, [passwordStrength])

  return (
    <div className="flex justify-between py-6 flex-1 gap-3">
      <div className="space-y-2 max-w-full">
        <span className="text-display-sm font-semibold break-all max-w-full whitespace-break-spaces">
          {password}
        </span>
        <div className="flex items-center gap-1">
          <span className="text-md">Mật khẩu mạnh:</span>
          <span
            className={clsx(
              passwordStrength?.score === 4 && 'text-[#079455]',
              passwordStrength?.score === 3 && 'text-[#DC6803]',
              (passwordStrength?.score === 2 || passwordStrength?.score <= 1) && 'text-[#D92D20]',
              'flex items-center gap-1 font-semibold'
            )}
          >
            <Shield className="w-6 h-6" />
            <span>{ParsePasswordScoreText(passwordStrength?.score)}</span>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-md">Thời gian phá được mật khẩu:</span>
          <span
            className={clsx(
              passwordStrength?.score === 4 && 'text-[#079455]',
              passwordStrength?.score === 3 && 'text-[#DC6803]',
              (passwordStrength?.score === 2 || passwordStrength?.score <= 1) && 'text-[#D92D20]',
              'flex items-center gap-1 font-semibold'
            )}
          >
            <span>{timeToCrack}</span>
          </span>
        </div>
      </div>
      <Refresh className="size-12 cursor-pointer flex-shrink-0" onClick={randomPassword} />
    </div>
  )
}
const passwordRules = [
  { label: 'Sử dụng chữ hoa (A-Z)', value: 'uppercase', disabled: false },
  { label: 'Sử dụng các chữ cái thường (a-z)', value: 'lowercase', disabled: false },
  { label: 'Sử dụng các chữ số (0-9)', value: 'number', disabled: false },
  { label: 'Sử dụng các ký hiệu (@! $% *)', value: 'special', disabled: false },
  { label: 'Tránh các ký tự không rõ ràng', value: 'ambiguous', disabled: false },
]

const defaultRule = ['uppercase', 'lowercase', 'number', 'special']
export default function PasswordGeneratorBanner({
  bannerTitle = 'Công cụ tạo mật khẩu mạnh ngẫu nhiên',
  bannerDescription = 'Sử dụng công cụ tạo mật khẩu mạnh, duy nhất, và hoàn toàn ngẫu nhiên của Locker để giúp bạn bảo mật các tài khoản trực tuyến an toàn hơn.',
}) {
  const router = useRouter()

  const [inputValue, setInputValue] = useState(12)
  const [rules, setRules] = useState(defaultRule)
  const [password, setPassword] = useState(defaultRule)
  const [passwordStrength, setPasswordStrength] = useState({})

  const [isOpenModalCopied, setIsOpenModalCopied] = useState(false)

  const onChange = (newValue) => {
    setInputValue(newValue)
  }

  useEffect(() => {
    initJsCore()
  }, [router])

  useEffect(() => {
    if (!window || !window.jsCore) return
    randomPassword()
  }, [inputValue, rules])

  const generateAndSetPassword = async (options) => {
    const result = await window.jsCore.passwordGenerationService.generatePassword(options)
    setPassword(result)
    setPasswordStrength(
      window.jsCore.passwordGenerationService.passwordStrength(result, ['cystack'])
    )
  }

  const loadListApp = async () => {
    const response = await fetchPasswordGeneratorApp()
    const results = response.data[DEFAULT_PASSWORD_NOTION_TABLE].collection.data.map((item) => {
      return {
        ...item,
        domain: item.Domain[0],
        name: item.Name[0],
        alias: item.Alias[0],
        slug: `app-${item.Alias[0]}`,
      }
    })
    return results
  }

  const initJsCore = async () => {
    if (!window) return
    if (!window.jsCore) {
      const jsCore = require('../../../core-js/index')
      window.jsCore = await jsCore.default()
    }
    const { slug } = router.query
    if (!slug) {
      await generateAndSetPassword({
        length: 12,
        uppercase: true,
        lowercase: true,
        number: true,
        special: true,
        ambiguous: false,
      })
      return
    }
    if (slug.includes('app-')) {
      const listApp = await loadListApp()
      const currentApp = listApp.find((item) => item.slug === slug)
      if (!!currentApp) {
        await generateAndSetPassword({
          length: 12,
          uppercase: true,
          lowercase: true,
          number: true,
          special: true,
          ambiguous: true,
        })
        return
      }
      return
    }
    if (slug.includes('characters')) {
      const numOfCharacters = parseInt(slug.split('-')[0])
      setInputValue(numOfCharacters)
      await generateAndSetPassword({
        length: numOfCharacters,
        uppercase: true,
        lowercase: true,
        number: true,
        special: true,
        ambiguous: true,
      })
      return
    }

    if (PASSWORD_GENERATOR_ALIAS.includes(slug)) {
      await generateAndSetPassword({
        length: slug === 'pin-code' ? 6 : 12,
        uppercase: [
          'with-uppercase',
          'mixed-case',
          'brute-force-resistance',
          'alphanumeric',
          'memorable',
        ].includes(slug),
        lowercase: [
          'with-lowercase',
          'mixed-case',
          'brute-force-resistance',
          'alphanumeric',
          'memorable',
        ].includes(slug),
        number: ['alphanumeric', 'numeric', 'pin-code', 'memorable'].includes(slug),
        special: slug === 'with-symbols',
        ambiguous: slug === 'memorable',
      })
      return
    }
  }

  const randomPassword = async () => {
    try {
      if (!window || !window.jsCore) return
      const result = await window.jsCore.passwordGenerationService.generatePassword({
        length: inputValue,
        uppercase: rules.includes('uppercase'),
        lowercase: rules.includes('lowercase'),
        number: rules.includes('number'),
        special: rules.includes('special'),
        ambiguous: rules.includes('ambiguous'),
      })
      setPassword(result)
      setPasswordStrength(
        window.jsCore.passwordGenerationService.passwordStrength(result, ['cystack'])
      )
    } catch (error) {
      console.log('error', error)
    }
  }

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(password)
    setIsOpenModalCopied(true)
  }

  const handleSavePassword = () => {
    router.push({
      pathname: '/vault',
      query: { password, nextStep: 'save' },
    })
  }
  return (
    <>
      <section className="py-12 lg:py-24">
        <div className="max-w-[820px] md:px-0 px-5 mx-auto">
          <h1 className="lg:text-display-xl text-display-lg text-heading text-center font-semibold">
            {bannerTitle}
          </h1>
          <div className="my-6 flex items-center gap-2 max-w-[300px] mx-auto">
            <span className="block h-[1px] bg-primary-HOVER w-full"></span>
            <span className="text-primary whitespace-nowrap flex-1 font-semibold">
              <CheckCircle className="w-6 h-6" />
            </span>
            <span className="block h-[1px] bg-primary-HOVER w-full"></span>
          </div>
          <p className="text-center text-lg text-heading-desctiption">{bannerDescription}</p>
          <Card
            className="mt-16 rounded-3xl overflow-hidden"
            type="inner"
            title={
              <CardTitle
                password={password}
                passwordStrength={passwordStrength}
                randomPassword={randomPassword}
              />
            }
          >
            <div className="md:pt-4 md:pb-6 pb-3 pt-2 md:px-2 px-0 flex-col md:flex-row flex justify-between gap-12 md:gap-28">
              <div className="space-y-4 w-full max-w-[300px]">
                <p className="font-semibold">
                  <span className="text-gray-500 text-lg">Độ dài</span>
                </p>
                <div className="flex items-center gap-3">
                  <InputNumber
                    min={8}
                    size="large"
                    className="w-14 h-11"
                    max={64}
                    value={inputValue}
                    onChange={onChange}
                  />
                  <Slider
                    min={8}
                    className="flex-1"
                    max={64}
                    onChange={onChange}
                    value={typeof inputValue === 'number' ? inputValue : 0}
                  />
                </div>
              </div>
              <div className="flex-1">
                <Checkbox.Group
                  options={passwordRules}
                  defaultValue={defaultRule}
                  value={rules}
                  className="space-y-3 *:w-full *:text-[##5A6176] *:text-md"
                  onChange={(value) => {
                    if (!value.length) return
                    setRules(value)
                  }}
                />
              </div>
            </div>
            <div className="flex items-center md:pt-6 md:pb-4 pt-4 pb-2 px-4 border-t border-l-0 border-r-0 border-b-0 -mx-4 md:-mx-8 md:px-8 border-solid border-[#E4E7EC] gap-4">
              <Button
                onClick={handleSavePassword}
                size="large"
                className="flex-grow h-12 rounded-lg font-medium"
              >
                Lưu với Locker
              </Button>
              <Button
                size="large"
                type="primary"
                className="flex-grow h-12 rounded-lg font-medium"
                onClick={handleCopyPassword}
              >
                Sao chép mật khẩu
              </Button>
            </div>
          </Card>
        </div>
        <ModalCopied isOpen={isOpenModalCopied} closeModal={() => setIsOpenModalCopied(false)} />
      </section>
    </>
  )
}
